import React from 'react';
import { Link } from 'react-router-dom';
import { createUseStyles } from 'react-jss';
import { S, A, DARK } from './tokens';

export type ButtonVariant = 'primary' | 'secondary' | 'quiet' | 'danger';

type Common = {
  variant?: ButtonVariant;
  size?: 'sm' | 'md';
  /** Material icon name shown before the label. */
  icon?: string;
  /** Shows a spinner and blocks clicks while an action runs. */
  busy?: boolean;
  className?: string;
  children?: React.ReactNode;
};

type AsButton = Common & Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'className' | 'children'> & { to?: undefined; href?: undefined };
type AsLink = Common & { to: string; href?: undefined; title?: string; onClick?: React.MouseEventHandler; 'data-testid'?: string };
type AsAnchor = Common & { href: string; to?: undefined; target?: string; rel?: string; title?: string; 'data-testid'?: string };

export type ButtonProps = AsButton | AsLink | AsAnchor;

const useStyles = createUseStyles({
  '@keyframes gxSpin': { to: { transform: 'rotate(360deg)' } },
  base: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    boxSizing: 'border-box',
    border: '1px solid transparent',
    borderRadius: A.radius,
    fontFamily: S.ui,
    fontWeight: 600,
    lineHeight: 1,
    whiteSpace: 'nowrap',
    textDecoration: 'none',
    cursor: 'pointer',
    userSelect: 'none',
    transition: 'background-color 120ms ease, border-color 120ms ease, color 120ms ease',
    '& .material-icons': { fontSize: 18, marginLeft: -2 },
    '&:focus-visible': A.focus,
    // A disabled <button> ignores clicks by itself and keeps its title tooltip; only links need pointer-events off.
    '&[disabled], &[aria-disabled="true"]': { opacity: 0.45, cursor: 'not-allowed' },
    '&[aria-disabled="true"]': { pointerEvents: 'none' }
  },
  md: { height: 36, padding: '0 14px', fontSize: 14 },
  sm: { height: 30, padding: '0 10px', fontSize: 13, '& .material-icons': { fontSize: 16 } },
  primary: {
    background: S.fg,
    color: S.onFg,
    '&:hover:not([disabled])': { background: S.fgHover },
    '&:active:not([disabled])': { background: S.fgActive }
  },
  secondary: {
    background: A.surface,
    color: S.fg,
    borderColor: S.fieldLine,
    '&:hover:not([disabled])': { background: S.chrome, borderColor: '#B4BAC2' },
    [DARK]: { '&:hover:not([disabled])': { borderColor: S.muted } }
  },
  quiet: {
    background: 'transparent',
    color: S.fg,
    '&:hover:not([disabled])': { background: S.chromeHover }
  },
  danger: {
    background: S.red,
    color: S.onFg,
    '&:hover:not([disabled])': { background: S.redHover }
  },
  spinner: {
    width: 14,
    height: 14,
    borderRadius: '50%',
    border: '2px solid currentColor',
    borderRightColor: 'transparent',
    animation: '$gxSpin 700ms linear infinite'
  }
});

// Props of this component, not of the <button> it renders.
const OWN_PROPS = ['variant', 'size', 'icon', 'busy', 'className', 'children', 'type', 'disabled'];

/**
 * One button for the whole admin. At most one primary per view; secondary
 * for the other actions; quiet inside toolbars; danger only to confirm a
 * destructive action.
 */
const Button = React.forwardRef<HTMLElement, ButtonProps>((props, ref) => {
  const c = useStyles();
  const { variant = 'secondary', size = 'md', icon, busy, className, children } = props;
  const cls = [c.base, c[size], c[variant], className].filter(Boolean).join(' ');
  const inner = (
    <>
      {busy ? <span className={c.spinner} aria-hidden /> : icon && <i className="material-icons" aria-hidden>{icon}</i>}
      {children}
    </>
  );
  if (props.to !== undefined) {
    const { to, title, onClick } = props as AsLink;
    return <Link innerRef={ref as React.Ref<HTMLAnchorElement>} to={to} className={cls} title={title} onClick={onClick} data-testid={(props as AsLink)['data-testid']}>{inner}</Link>;
  }
  if (props.href !== undefined) {
    const { href, target, rel, title } = props as AsAnchor;
    return (
      <a ref={ref as React.Ref<HTMLAnchorElement>} href={href} target={target} rel={rel || (target === '_blank' ? 'noopener noreferrer' : undefined)} className={cls} title={title} data-testid={(props as AsAnchor)['data-testid']}>
        {inner}
      </a>
    );
  }
  const rest: Record<string, unknown> = { ...(props as AsButton) };
  OWN_PROPS.forEach((k) => { delete rest[k]; });
  const { type, disabled } = props as AsButton;
  return (
    <button {...rest} ref={ref as React.Ref<HTMLButtonElement>} type={type || 'button'} className={cls} disabled={disabled || busy} aria-busy={busy || undefined}>
      {inner}
    </button>
  );
});

Button.displayName = 'Button';

export default Button;
